// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Organizaciones (M1 Structure)
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import { AppHeader } from '../../../../shared/components/AppHeader';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { OrganizacionesArbol } from '../components/OrganizacionesArbol';
import {
  fetchOrganizaciones,
  fetchOrganizacion,
  fetchDepartamentos,
  fetchPuestos,
  type OrganizacionResumen,
  type PuestoItem,
} from '../../../../shared/services/api';
// 0615i: tab Organizaciones es solo lectura. Mutacion vive en tab Administracion.
// Eliminados imports de crear/actualizar/eliminar + ConfirmDialog + useHasNivelModulo +
// fetchNivelesPuesto / request (ya no necesarios sin los modales internos).

interface DeptRow {
  id: string;
  nombre: string;
  codigo?: string;
  padreId?: string | null;
  organizacionId?: string;
}

// ─── Styles ───
// 0615i: estilos de inputs/labels/btnPrimary eliminados con los modales internos.
// Solo conservamos el secundario (boton "Volver" en OrgDetailView).
const btnSecondary: React.CSSProperties = {
  padding: '8px 16px',
  border: '1px solid #D1D5DB',
  borderRadius: 6,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: 'inherit',
};

// ═══════════════════════════════════════════════════════════════
// ORG DETAIL PAGE
// ═══════════════════════════════════════════════════════════════
function OrgDetailView({ orgId, onBack }: { orgId: string; onBack: () => void }) {
  const { t } = useI18n();
  // 0615i ADR-024 + filosofia CEO: tabs operativas son lectura/consulta.
  // Toda mutacion (crear/editar/eliminar org, dept, puesto) vive en la tab
  // Administracion del modulo. Aqui solo se ve el detalle de la org.
  const [org, setOrg] = useState<OrganizacionResumen | null>(null);
  const [departamentos, setDepartamentos] = useState<DeptRow[]>([]);
  const [puestos, setPuestos] = useState<PuestoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'departamentos' | 'puestos'>('departamentos');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, deptRes, puestoRes] = await Promise.all([
        fetchOrganizacion(orgId),
        fetchDepartamentos({ organizacionId: orgId }),
        fetchPuestos({ organizacionId: orgId }),
      ]);
      setOrg(orgRes.data as OrganizacionResumen);
      setDepartamentos(deptRes.data as DeptRow[]);
      setPuestos(puestoRes.data as PuestoItem[]);
    } catch (err) {
      console.error('Error loading org detail:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <LoadingSpinner text={t.common.cargando} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={onBack} style={btnSecondary}>
          ← {t.common.volver}
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: 'var(--color-primario)' }}>{org?.nombre}</h2>
          <div style={{ fontSize: 12, color: '#5A6171' }}>
            {org?.tipo?.replace(/_/g, ' ')} — {departamentos.length} departamentos, {puestos.length}{' '}
            puestos
          </div>
        </div>
      </div>

      {/* Hierarchy info */}
      {(() => {
        const orgAny = org as any;
        const padre = orgAny?.organizacionPadre;
        const hijas: any[] = orgAny?.hijas ?? [];
        if (!padre && hijas.length === 0) return null;
        return (
          <div
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, fontSize: 13 }}
          >
            {padre && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#5A6171', fontWeight: 600 }}>
                  {t.structure.orgs.parentOrgLabel}:
                </span>
                <span
                  style={{ color: '#2F5496', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => {
                    onBack();
                  }}
                >
                  {padre.nombre}
                </span>
              </div>
            )}
            {hijas.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ color: '#5A6171', fontWeight: 600 }}>
                  {t.structure.orgs.subsidiaries}:
                </span>
                {hijas.map((h: any) => (
                  <span
                    key={h.id}
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 11,
                      fontWeight: 600,
                      background: '#EFF6FF',
                      color: '#1D4ED8',
                    }}
                  >
                    {h.nombre}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #E2E6ED', marginBottom: 20 }}>
        {(['departamentos', 'puestos'] as const).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'transparent',
              borderBottom:
                tab === tabKey ? '2px solid var(--color-primario)' : '2px solid transparent',
              color: tab === tabKey ? 'var(--color-primario)' : '#5A6171',
              fontWeight: tab === tabKey ? 600 : 400,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: -2,
            }}
          >
            {tabKey === 'departamentos'
              ? t.structure.orgs.departmentTab(departamentos.length)
              : t.structure.orgs.positionTab(puestos.length)}
          </button>
        ))}
      </div>

      {/* Departamentos tab — 0615i: solo lectura, mutacion en tab Administracion. */}
      {tab === 'departamentos' && (
        <div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t.common.nombre}</th>
                <th style={thStyle}>{t.common.codigo}</th>
                <th style={thStyle}>{t.structure.orgs.parentDept}</th>
              </tr>
            </thead>
            <tbody>
              {departamentos.map((d) => (
                <tr key={d.id} style={trStyle}>
                  <td style={tdStyle}>{d.nombre}</td>
                  <td style={tdStyle}>{d.codigo || '—'}</td>
                  <td style={tdStyle}>
                    {departamentos.find((p) => p.id === d.padreId)?.nombre || '—'}
                  </td>
                </tr>
              ))}
              {departamentos.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>
                    {t.structure.orgs.noDepts}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Puestos tab — 0615i: solo lectura, mutacion en tab Administracion. */}
      {tab === 'puestos' && (
        <div>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t.structure.tituloLabel}</th>
                <th style={thStyle}>{t.common.codigo}</th>
                <th style={thStyle}>{t.structure.nivelLabel}</th>
                <th style={thStyle}>{t.structure.departamentoLabel}</th>
                <th style={thStyle}>{t.structure.orgs.person}</th>
              </tr>
            </thead>
            <tbody>
              {puestos.map((p) => {
                const persona = p.asignaciones?.find((a) => a.activo)?.persona;
                return (
                  <tr key={p.id} style={trStyle}>
                    <td style={tdStyle}>{p.titulo}</td>
                    <td style={tdStyle}>{p.codigo || '—'}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '2px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          background: '#F3F4F6',
                        }}
                      >
                        {p.nivel?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={tdStyle}>{p.departamento?.nombre || '—'}</td>
                    <td style={tdStyle}>
                      {persona ? (
                        `${persona.nombre} ${persona.apellidos}`
                      ) : (
                        <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                          {t.structure.vacantLabel}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {puestos.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9CA3AF' }}>
                    {t.structure.orgs.noPositions}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export function OrganizacionesPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useI18n();
  // 0615i ADR-024 + filosofia CEO: tab Organizaciones es solo lectura.
  // Crear/editar/eliminar org vive en tab Administracion.
  const [orgs, setOrgs] = useState<OrganizacionResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'arbol' | 'tabla'>(
    () => (localStorage.getItem('vianet-orgs-view') as 'arbol' | 'tabla') ?? 'arbol',
  );
  const toggleOrgView = (mode: 'arbol' | 'tabla') => {
    setViewMode(mode);
    localStorage.setItem('vianet-orgs-view', mode);
  };

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchOrganizaciones();
      setOrgs(res.data);
    } catch (err) {
      console.error('Error loading orgs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  // 0615i: handleDelete + confirmState eliminados - mutacion en tab Administracion.

  if (selectedOrgId) {
    return (
      <div style={{ background: 'var(--wl-background, #ECEEF2)', minHeight: '100vh' }}>
        {!embedded && <AppHeader modulo="Structure" titulo="Organizaciones" />}
        <div style={{ padding: '20px 32px', maxWidth: 1200, margin: '0 auto' }}>
          <OrgDetailView orgId={selectedOrgId} onBack={() => setSelectedOrgId(null)} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--wl-background, #ECEEF2)', minHeight: '100vh' }}>
      {!embedded && <AppHeader modulo="Structure" titulo="Organizaciones" />}

      <div style={{ padding: '20px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              border: '1px solid #D1D5DB',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <button
              onClick={() => toggleOrgView('arbol')}
              style={{
                padding: '6px 10px',
                border: 'none',
                background: viewMode === 'arbol' ? '#0E243E' : '#fff',
                color: viewMode === 'arbol' ? '#fff' : '#6B7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              title={t.structure.orgs.vistaArbol}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 3v9m0 0l-4-3m4 3l4-3M6 15h12" />
              </svg>
            </button>
            <button
              onClick={() => toggleOrgView('tabla')}
              style={{
                padding: '6px 10px',
                border: 'none',
                background: viewMode === 'tabla' ? '#0E243E' : '#fff',
                color: viewMode === 'tabla' ? '#fff' : '#6B7280',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              title={t.structure.orgs.vistaTabla}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          </div>
          {/* 0615i: boton "Nueva organizacion" eliminado. Crear en tab Administracion. */}
        </div>
        {viewMode === 'arbol' ? (
          <OrganizacionesArbol />
        ) : loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
            {t.structure.orgs.loadingOrgs}
          </div>
        ) : orgs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>
            {t.structure.orgs.noOrgs}
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t.common.nombre}</th>
                <th style={thStyle}>{t.common.tipo}</th>
                <th style={thStyle}>{t.structure.orgs.departments}</th>
                <th style={thStyle}>{t.structure.orgs.positions}</th>
                <th style={thStyle}>{t.common.estado}</th>
                <th style={{ ...thStyle, width: 180 }}>{t.common.acciones}</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id} style={trStyle}>
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: 600,
                      color: 'var(--color-primario)',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedOrgId(o.id)}
                  >
                    {o.nombre}
                  </td>
                  <td style={tdStyle}>{o.tipo?.replace(/_/g, ' ')}</td>
                  <td style={tdStyle}>{o._count?.departamentos ?? 0}</td>
                  <td style={tdStyle}>{o._count?.puestos ?? 0}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 4,
                        fontSize: 10,
                        fontWeight: 600,
                        background: o.activo === true ? '#F0FDF4' : '#FEF2F2',
                        color: o.activo === true ? '#16A34A' : '#DC2626',
                      }}
                    >
                      {o.activo === true ? t.structure.orgs.active : t.structure.orgs.inactive}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    {/* 0615i: solo "Ver" (lectura). Editar/Eliminar en tab Administracion. */}
                    <button onClick={() => setSelectedOrgId(o.id)} style={actionBtn}>
                      {t.common.ver}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Shared Styles (0615i: modal styles eliminados con los modales internos) ───
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  background: '#fff',
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#fff',
  background: '#0E243E',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};
const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontSize: 13,
  color: '#374151',
  borderBottom: '1px solid #F2F4F6',
};
const trStyle: React.CSSProperties = {};
const actionBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-secundario)',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: 'inherit',
  fontWeight: 500,
  marginRight: 8,
};
